"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider } from "@/integrations/wordpress/WordPressMenuProvider";
const NAV_ITEMS = [{
  label: "Inicio",
  href: "/"
}, {
  label: "Nosotros",
  href: "/AboutUsPage"
}, {
  label: "Servicios",
  href: "/#servicios"
}, {
  label: "Capacitación",
  href: "/TrainingPage"
}, {
  label: "Tecnología",
  href: "/TechnologyPage"
}, {
  label: "Contacto",
  href: "/ContactPage"
}];
function DesktopMenu() {
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-0">
        {NAV_ITEMS.map((item, i) => <NavigationMenuItem data-index={i} key={item.label} className="flex items-center">
            <Link to={item.href} className="text-white text-xs uppercase tracking-widest font-mono px-4 py-2 hover:text-secondary transition-colors duration-300">
              {item.label}
            </Link>
            {i < NAV_ITEMS.length - 1 && <span aria-hidden="true" className="h-3 w-px bg-white/20" />}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  return <nav className="flex flex-col gap-6 px-6 pt-4">
      {NAV_ITEMS.map((item, i) => <Link data-index={i} key={item.label} to={item.href} onClick={onNavigate} className="text-white font-default font-bold text-4xl tracking-tighter hover:text-secondary transition-colors duration-300" style={{
      animation: `fadeSlideIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${i * 100}ms both`
    }}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function NavegaciNPrincipal() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="4725" id="navegación-principal" className={`fixed top-0 z-50 w-full transition-all duration-500 ${scrolled ? "bg-black/90 backdrop-blur-md border-b border-white/10" : "bg-black border-b border-transparent"}`}>
      <style>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      <div className={`mx-auto max-w-7xl px-6 lg:px-10 flex items-center justify-between transition-all duration-500 ${scrolled ? "py-4" : "py-6"}`}>
        <Link to="/" className="flex items-center" aria-label="POSEIDON home">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697018/img/Lgotipo de poseidon.png" alt="POSEIDON Logo" className="h-12 w-auto opacity-90" loading="lazy" decoding="async" />
        </Link>

        <div className="hidden lg:flex items-center">
          <MenuProvider menu_id="16">
            <DesktopMenu />
          </MenuProvider>
        </div>

        <div className="hidden lg:flex items-center">
          <Button asChild variant="outline" className="rounded-md border border-white/20 bg-transparent text-white hover:bg-secondary hover:text-secondary-foreground font-mono uppercase text-xs tracking-widest px-6 py-2.5 h-auto transition-all duration-300">
            <Link to="/ContactPage">Cotizar</Link>
          </Button>
        </div>

        <div className="flex lg:hidden items-center">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Abrir menú" className="text-white hover:bg-white/10 rounded-md">
                <Menu className="size-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-black border-l border-white/10 w-full sm:max-w-md p-0">
              <SheetHeader className="px-6 pt-8 pb-4">
                <SheetTitle className="font-mono uppercase tracking-widest text-sm text-white/60 text-left">
                  Navegación
                </SheetTitle>
              </SheetHeader>

              <div className="flex flex-col h-full">
                <MenuProvider menu_id="16">
                  <MobileMenu onNavigate={() => setMobileOpen(false)} />
                </MenuProvider>

                <div className="mt-auto px-6 pb-10 pt-8">
                  <Button asChild variant="outline" className="w-full rounded-md border border-secondary bg-secondary text-secondary-foreground hover:bg-secondary/90 font-default font-bold text-base py-4 h-auto" onClick={() => setMobileOpen(false)}>
                    <Link to="/ContactPage">Solicitar Cotización</Link>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}